import type {
  ComputedJavaScriptVariableState,
  SetVariableStringTypePayload,
} from '@unifyapps/defs/types/javascriptVariable';
import _isUndefined from 'lodash/isUndefined';

/**
 * Any change here should be reflected in the METHODS_DOCUMENTATION.md file
 */
export function getNextPageVariableStateForString({
  variableState,
  operationDetails,
  onError,
}: {
  variableState: ComputedJavaScriptVariableState<unknown>;
  operationDetails: {
    operation?: SetVariableStringTypePayload['operationDetails']['operation'];
    value?: unknown;
  };
  onError?: (message: string) => void;
}) {
  // a bad payload is logged to the dev console and surfaced to Pulse (via onError) as an error,
  // then the variable is left unchanged. Only the invalid-payload branches call this.
  const reportPayloadError = (message: string) => {
    console.error(message);
    onError?.(message);
  };
  switch (operationDetails.operation) {
    case 'SET': {
      // SET allows undefined to reset the variable, or string to set it
      if (!_isUndefined(operationDetails.value) && typeof operationDetails.value !== 'string') {
        reportPayloadError('operationDetails.value should be string or undefined for set operation');
        return variableState;
      }
      return {
        ...variableState,
        value: operationDetails.value,
      };
    }
    case 'APPEND': {
      // validate current state is string or undefined (for initialization)
      if (!_isUndefined(variableState.value) && typeof variableState.value !== 'string') {
        reportPayloadError('variableState.value should be string or undefined for append operation');
        return variableState;
      }
      // validate input value is string or undefined
      if (!_isUndefined(operationDetails.value) && typeof operationDetails.value !== 'string') {
        reportPayloadError('operationDetails.value should be string or undefined for append operation');
        return variableState;
      }

      return {
        ...variableState,
        value: `${variableState.value ?? ''}${operationDetails.value ?? ''}`,
      };
    }
    case 'PREPEND': {
      // validate current state is string or undefined (for initialization)
      if (!_isUndefined(variableState.value) && typeof variableState.value !== 'string') {
        reportPayloadError('variableState.value should be string or undefined for prepend operation');
        return variableState;
      }
      // validate input value is string or undefined
      if (!_isUndefined(operationDetails.value) && typeof operationDetails.value !== 'string') {
        reportPayloadError('operationDetails.value should be string or undefined for prepend operation');
        return variableState;
      }
      return {
        ...variableState,
        value: `${operationDetails.value ?? ''}${variableState.value ?? ''}`,
      };
    }
    default:
      return variableState;
  }
}
